<template>
  <div id="communications_list">
    <div
      v-if="$store.getters.isMaitreComptaRegion"
      class="buttons"
    >
      <ButtonPopover
        v-model="listOpen"
        :label="`${globalMessages.length}`"
        variant="solid"
        color="primary"
        icon="left"
        placement="bottom"
        trigger="click"
      >
        <template #left-icon>
          <UilEye />
        </template>
        <div class="message-list">
          <div class="text-center">
            {{ $t("communications.il-y-a-x-messages-actifs", { count: globalMessages.length, }) }}
          </div>
          <ul>
            <li
              v-for="(message, index) in globalMessages"
              :key="message.id ?? index"
            >
              "{{ message.titre }}"
              - {{ $moment(message.date_modification || message.date_creation).format($t("global.format-date")) }}:
              <ul
                v-for="page in verbosePages(message)"
                :key="page"
              >
                <li>{{ page }}</li>
              </ul>
            </li>
          </ul>
        </div>
      </ButtonPopover>
      <ButtonClassic
        label="Ajouter un message"
        variant="ghost"
        color="primary"
        icon="left"
        @click="addMessage"
      >
        <template #left-icon>
          <UilCommentAlt />
        </template>
      </ButtonClassic>
    </div>
    <Communication
      v-if="messagesAdded"
      v-model="messagesAdded"
      @delete="messagesAdded = null"
      @save="handleNewMessage"
    />
    <Slider
      v-if="showSlider"
      :autoplay="autoplay"
    >
      <template #slider-items>
        <SliderCard
          v-for="(message, index) in messageAffiches"
          :key="message.id ?? index"
        >
          <template #custom>
            <Communication
              v-model="messageAffiches[index]"
              @delete="removeMessage()"
              @editing="editingChange"
            />
          </template>
        </SliderCard>
      </template>
    </Slider>
    <InfiniteLoader v-else />
  </div>
</template>

<script setup lang="ts">
// #region imports =======================
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import {
  ButtonClassic, ButtonPopover, InfiniteLoader, Slider, SliderCard,
} from "@lde/core_lde_vue";
import { UilCommentAlt, UilEye } from "@lde/vue-unicons";

import Communication from "@/components/communications/Communication.ts.vue";
import CommunicationModel from "@/domains/communications/communication";

import { buildPages } from "./pages";

// #endregion

// #region Données et état =======================
const messages = ref<CommunicationModel[]>([]);
const { t } = useI18n();
const route = useRoute();
const pages = buildPages();
const autoplay = ref(true);
const messagesAdded = ref<CommunicationModel | null>(null);
const listOpen = ref(false);
const showSlider = ref(true);
// #endregion

// #region Filtrage et affichage des messages =======================
const routesName: Record<string, string> = {};
pages.flat().forEach((page) => {
  [page, ...page.subPages ?? []].forEach((p) => {
    routesName[p.key] = t(p.labelKey);
  });
});

const messageAffiches = computed(() => {
  const pageName = route.name as string;
  return messages.value.filter(
    (message) => message.pages.includes("all") || message.pages.some((page) => pageName?.startsWith(page)),
  );
});
const globalMessages = computed(() => messages.value.filter((message) => message.id !== undefined));

const verbosePages = (message: CommunicationModel) => {
  if (message.pages.includes("all")) {
    return ["Toutes les pages"];
  }
  return message.pages.map((page) => routesName[page] || page);
};
// #endregion

// #region Chargement des messages =======================
onMounted(() => {
  fetchMessages();
});

const reloadSlider = () => {
  showSlider.value = false;
  setTimeout(() => {
    showSlider.value = true;
  }, 250);
};

const fetchMessages = async () => {
  // Hack dégeu: le slider est full bugé quand on retire des éléments à la volée...
  messages.value = await CommunicationModel.fetchAll();
  reloadSlider();
};
// #endregion

// #region Ajout, suppression et édition =======================
const removeMessage = () => {
  fetchMessages();
};

const addMessage = () => {
  messagesAdded.value = new CommunicationModel({
    texte: "", titre: "", couleur: "info", pages: ["all"],
  });
};

const handleNewMessage = () => {
  messagesAdded.value = null;
  fetchMessages();
};

const editingChange = (value: boolean) => {
  autoplay.value = !value;
};
// #endregion
</script>

<style lang="scss">
@use "@lde/core_lde_vue/dist/assets/styles/_variables.scss" as var;

#communications_list {
  position: relative;
  z-index: 30;
  margin-bottom: var.$space-small;
  .communication {
    width: 100%;
  }
  .communication + .communication {
    margin-top: var.$space-small;
  }
  > .buttons {
    display: flex;
    justify-content: flex-end;
    margin-bottom: var.$space-x-tiny;
    gap: var.$space-x-tiny;
  }

  .message-list {
    font-size: var.$font-regular;
    padding: var.$space-xx-tiny;
    ul {
      margin-top: var.$space-xx-tiny;
      padding-left: var.$space-small;
    }
  }

  .text-center {
    text-align: center;
  }

  .toast {
    margin-bottom: 0;
  }

  .slider {
    display: flex;
    flex-direction: column-reverse;
    .slider-card {
      box-shadow: none;
      min-height: 0;
    }
    .pagination-bubble {
      display: flex;
      justify-content: center;
    }
    .slider-items {
      height: unset;
      min-height: unset;
    }
  }
}
</style>
